import {
  GridLayout,
  ChatMessage as LiveKitChatMessage,
  ParticipantTile,
  TrackReferenceOrPlaceholder,
  useEnsureTrackRef,
} from "@livekit/components-react"
import { Box } from "@mui/material"
import { Track, TrackPublication } from "livekit-client"
import React, { Fragment } from "react"
import { Agent } from "../../types/Agent"
import { CallCardHeader } from "../CallChrome/CallCardHeader"
import { UserVideoLiveKit } from "../UserVideo/UserVideoLiveKit"
import { AgentBackgroundVideo } from "./AgentBackgroundVideo"
import CallLanguage from "./CallLanguage"
import CustomCallTimer from "./CustomCallTimer"
import { CustomChatBox } from "./CustomChatBox"
import { CustomControlsBar } from "./CustomControlsBar"

function AgentAwareParticipantTile({ agent }: { agent: Agent }) {
  const trackRef = useEnsureTrackRef()
  const isAgentCamera =
    trackRef.participant?.isAgent && trackRef.source === Track.Source.Camera
  const bgUrl = agent.agent_background_url

  if (!isAgentCamera || !bgUrl) {
    return <ParticipantTile trackRef={trackRef} />
  }

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <ParticipantTile trackRef={trackRef} />
      <AgentBackgroundVideo trackRef={trackRef} imageUrl={bgUrl} />
    </div>
  )
}

type LayoutProps = {
  agent: Agent
  tracks: TrackReferenceOrPlaceholder[]
  showChat: boolean
  audioState: boolean
  videoState: boolean
  isEmbed: boolean
  localCameraTrack: TrackPublication
  handleDisconnect: () => void
  toggleChat: () => void
  duration: number
  maxDuration: number
  closeChat: () => void
  chatMessages: LiveKitChatMessage[]
  isSending: boolean
  send: (message: string) => Promise<LiveKitChatMessage>
}

const visibleTracks = (tracks: TrackReferenceOrPlaceholder[]) =>
  tracks.filter(
    (track) =>
      !track.participant.isLocal &&
      !(
        track.participant.isAgent &&
        track.participant.attributes &&
        track.participant.attributes["lk.agent.state"]
      ),
  )

export const CallLayout = React.memo(function CallLayout({
  agent,
  tracks,
  showChat,
  localCameraTrack,
  handleDisconnect,
  toggleChat,
  duration,
  maxDuration,
  closeChat,
  chatMessages,
  isSending,
  send,
  audioState,
  videoState,
}: LayoutProps) {
  return (
    <Fragment>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          "& .lk-grid-layout": { height: "100%", width: "100%" },
        }}
      >
        <GridLayout tracks={visibleTracks(tracks)} style={{ height: "100%" }}>
          <AgentAwareParticipantTile agent={agent} />
        </GridLayout>
      </Box>

      <CallCardHeader
        agent={agent}
        secondary={
          <CustomCallTimer duration={duration} maxDuration={maxDuration} />
        }
        right={<CallLanguage agent={agent} />}
      />

      <UserVideoLiveKit track={localCameraTrack} />

      <CustomControlsBar
        showChat={showChat}
        disconnect={handleDisconnect}
        toggleChat={toggleChat}
        audioState={audioState}
        videoState={videoState}
      />

      <CustomChatBox
        showChat={showChat}
        closeChat={closeChat}
        chatMessages={chatMessages}
        isSending={isSending}
        send={send}
      />
    </Fragment>
  )
})
